import { Theme, ThemeUIStyleObject } from "theme-ui";

const baseColors = {
  blue: "#1542cd",
  purple: "#745ddf",
  cyan: "#2eb6ea",
  green: "#28c081",
  yellow: "#fd9d28",
  red: "#dc2c10",
  lightRed: "#ff755f",
  lightYellow: "#F9E39D",
  yellow_100: "#FCF1CE",
  yellow_200: "#F0B90B",
  yellow_300: "#D8A60A",
  yellow_400: "#C09409",
  lightWhite: "#ffffff80",
  lightGrey: "#E5E5E5",
  grey_100: "#B3B3B3",
  black: "#000000",
  white: "#ffffff",
  lightBlack: "#666666",
  red_100: "#B00020",
  brown: "#804C3B"
};

const colors = {
  primary: baseColors.black,
  secondary: baseColors.purple,
  accent: baseColors.cyan,

  success: baseColors.green,
  warning: baseColors.yellow,
  danger: baseColors.red,
  dangerHover: baseColors.lightRed,
  info: baseColors.blue,
  invalid: "pink",

  text: "#1F2937", // Modern dark gray for better readability
  background: "white",
  muted: "#F3F4F6" // Softer muted color
};

const buttonBase: ThemeUIStyleObject = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "Avenir",
  letterSpacing: 1.25,
  ":enabled": { cursor: "pointer" }
};

const button: ThemeUIStyleObject = {
  ...buttonBase,

  px: "32px",
  py: "12px",

  color: "white",
  border: 1,

  fontWeight: "medium",
  textTransform: "capitalize",

  ":disabled": {
    opacity: 0.5
  }
};

const iconButton: ThemeUIStyleObject = {
  ...buttonBase,

  padding: 0,
  width: "40px",
  height: "40px",

  background: "none",

  ":disabled": {
    color: "text",
    opacity: 0.25
  }
};

const cardHeadingFontSize = 24;

const cardGapX = [0, 3, 4];
const cardGapY = [3, 3, 4];

const card: ThemeUIStyleObject = {
  position: "relative",
  mt: cardGapY,
  // border: 1,
  boxShadow: [1, null, 2]
};

const infoCard: ThemeUIStyleObject = {
  ...card,

  padding: 3,

  // borderColor: "rgba(122,199,240,0.4)",
  background: "linear-gradient(180deg, #FBEAB6 0%, rgba(251, 234, 182, 0) 100%)",

  h2: {
    mb: 2,
    fontSize: cardHeadingFontSize
  }
};

const formBase: ThemeUIStyleObject = {
  display: "block",
  width: "auto",
  flexShrink: 0,
  padding: 2,
  fontSize: 3
};

const formCell: ThemeUIStyleObject = {
  ...formBase,

  bg: "background",
  border: 1,
  borderColor: "muted",
  borderRadius: 0,
  boxShadow: [1, 2]
};

const overlay: ThemeUIStyleObject = {
  position: "absolute",

  left: 0,
  top: 0,
  width: "100%",
  height: "100%"
};

const modalOverlay: ThemeUIStyleObject = {
  position: "fixed",

  left: 0,
  top: 0,
  width: "100vw",
  height: "100vh"
};

const headerGradient: ThemeUIStyleObject = {
  background: "#EAEAE5"
};

const theme: Theme = {
  breakpoints: ["48em", "52em", "64em"],

  space: [0, 4, 8, 16, 32, 64, 128, 256, 512],

  fonts: {
    body: ["Inter", "-apple-system", "BlinkMacSystemFont", "Segoe UI", "sans-serif"].join(", "),
    heading: ["Inter", "-apple-system", "BlinkMacSystemFont", "Segoe UI", "sans-serif"].join(", "),
    monospace: ["JetBrains Mono", "Monaco", "Consolas", "monospace"].join(", ")
  },

  fontSizes: [12, 14, 16, 20, 24, 32, 48, 64, 96],

  fontWeights: {
    body: 400,
    heading: 600,

    light: 200,
    medium: 500,
    bold: 600
  },

  lineHeights: {
    body: 1.5,
    heading: 1.25
  },

  colors,

  borders: [0, "1px solid", "2px solid"],

  shadows: [
    "0",
    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)", // Subtle
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)", // Medium
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)" // Large
  ],

  text: {
    address: {
      fontFamily: "monospace",
      fontSize: 1
    }
  },

  buttons: {
    primary: {
      ...button,

      background: `linear-gradient(135deg, ${baseColors.yellow_200} 0%, ${baseColors.yellow_300} 100%)`,
      borderColor: baseColors.yellow_200,
      color: "#000000",
      fontSize: 1,
      fontWeight: "600",
      borderRadius: "12px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      transition: "all 0.2s ease",

      ":enabled:hover": {
        background: `linear-gradient(135deg, ${baseColors.yellow_300} 0%, ${baseColors.yellow_400} 100%)`,
        borderColor: baseColors.yellow_300,
        transform: "translateY(-2px)",
        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.15)"
      },
      ":enabled:focus": {
        background: `linear-gradient(135deg, ${baseColors.yellow_300} 0%, ${baseColors.yellow_400} 100%)`,
        borderColor: baseColors.yellow_400,
        outline: "none",
        boxShadow: `0 0 0 3px ${baseColors.yellow_100}`
      },
      ":enabled:active": {
        transform: "translateY(0)"
      },
      ":disabled": {
        background: baseColors.lightGrey,
        borderColor: baseColors.lightGrey,
        cursor: "not-allowed",
        color: baseColors.grey_100,
        boxShadow: "none"
      }
    },
    small: {
      ...button,

      bg: baseColors.lightWhite,
      borderColor: baseColors.yellow_200,
      color: baseColors.black,
      border: 1,
      fontSize: 0,
      fontWeight: "600",
      borderRadius: "10px",
      transition: "all 0.2s ease",

      ":enabled:hover": {
        bg: baseColors.yellow_100,
        borderColor: baseColors.yellow_200,
        transform: "translateY(-1px)"
      },
      ":enabled:focus": {
        bg: baseColors.lightYellow,
        borderColor: baseColors.yellow_200,
        outline: "none"
      },
      ":enabled:active": {
        transform: "translateY(0)"
      },
      ":disabled": {
        bg: baseColors.lightWhite,
        borderColor: baseColors.lightGrey,
        color: baseColors.lightGrey,
        cursor: "not-allowed"
      }
    },

    outline: {
      ...button,
      bg: "transparent",
      borderColor: baseColors.yellow_200,
      color: baseColors.black,
      border: "2px solid",
      borderRadius: "12px",
      fontWeight: "600",
      transition: "all 0.2s ease",

      ":enabled:hover": {
        bg: baseColors.yellow_100,
        borderColor: baseColors.yellow_300,
        transform: "translateY(-2px)",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
      },
      ":enabled:focus": {
        outline: "none",
        boxShadow: `0 0 0 3px ${baseColors.yellow_100}`
      },
      ":enabled:active": {
        transform: "translateY(0)"
      },
      ":disabled": {
        borderColor: baseColors.lightGrey,
        color: baseColors.lightGrey,
        cursor: "not-allowed"
      }
    },

    cancel: {
      ...button,
      bg: "white",
      borderColor: "#E5E7EB",
      color: baseColors.black,
      border: "1px solid",
      fontSize: 1,
      fontWeight: "600",
      borderRadius: "12px",
      transition: "all 0.2s ease",

      ":enabled:hover": {
        bg: "#F9FAFB",
        borderColor: "#D1D5DB",
        transform: "translateY(-1px)"
      },
      ":enabled:focus": {
        outline: "none",
        boxShadow: "0 0 0 3px #F3F4F6"
      },
      ":enabled:active": {
        transform: "translateY(0)"
      },
      ":disabled": {
        bg: baseColors.lightGrey,
        borderColor: baseColors.lightGrey,
        color: baseColors.grey_100,
        cursor: "not-allowed"
      }
    },

    danger: {
      ...button,

      background: `linear-gradient(135deg, ${baseColors.red} 0%, #B00020 100%)`,
      borderColor: baseColors.red,
      color: "white",
      fontWeight: "600",
      borderRadius: "12px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      transition: "all 0.2s ease",

      ":enabled:hover": {
        background: `linear-gradient(135deg, #B00020 0%, #8B0018 100%)`,
        borderColor: "dangerHover",
        transform: "translateY(-2px)",
        boxShadow: "0 10px 15px -3px rgba(220, 44, 16, 0.2)"
      },
      ":enabled:focus": {
        outline: "none",
        boxShadow: "0 0 0 3px rgba(220, 44, 16, 0.2)"
      },
      ":enabled:active": {
        transform: "translateY(0)"
      },
      ":disabled": {
        background: baseColors.lightGrey,
        borderColor: baseColors.lightGrey,
        cursor: "not-allowed",
        boxShadow: "none"
      }
    },

    icon: {
      ...iconButton,
      color: baseColors.blue,
      borderRadius: "8px",
      transition: "all 0.2s ease",
      ":enabled:hover": {
        color: "accent",
        bg: "#F0F9FF",
        transform: "scale(1.05)"
      },
      ":enabled:active": {
        transform: "scale(0.95)"
      }
    },

    dangerIcon: {
      ...iconButton,
      color: "danger",
      borderRadius: "8px",
      transition: "all 0.2s ease",
      ":enabled:hover": {
        color: "dangerHover",
        bg: "#FEE2E2",
        transform: "scale(1.05)"
      },
      ":enabled:active": {
        transform: "scale(0.95)"
      }
    },

    titleIcon: {
      ...iconButton,
      color: "text",
      borderRadius: "8px",
      transition: "all 0.2s ease",
      ":enabled:hover": {
        color: "success",
        bg: "#F0FDF4",
        transform: "scale(1.05)"
      },
      ":enabled:active": {
        transform: "scale(0.95)"
      }
    }
  },

  cards: {
    primary: {
      ...card,

      padding: 0,
      borderRadius: "16px",
      border: "1px solid",
      borderColor: "#E5E7EB",
      bg: "white",
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
      transition: "all 0.2s ease",

      ":hover": {
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        borderColor: "#D1D5DB"
      },

      "> h2": {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",

        height: "56px",

        pl: 3,
        py: 2,
        pr: 2,

        fontWeight: "700",

        bg: baseColors.black,
        fontFamily: "Inter",
        borderTopLeftRadius: "16px",
        borderTopRightRadius: "16px",

        fontSize: cardHeadingFontSize,
        color: baseColors.white
      }
    },

    info: {
      ...infoCard,

      borderRadius: "16px",
      border: "1px solid",
      borderColor: "#FEF3C7",
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",

      display: "block",
      "@media screen and (max-width: 47.9375em)": {
        display: "none"
      }
    },

    infoPopup: {
      ...infoCard,

      position: "fixed",
      top: 0,
      right: 3,
      left: 3,
      mt: "72px",
      height: "80%",
      overflowY: "scroll"
    },

    tooltip: {
      padding: 2,
      borderRadius: "8px",
      bg: baseColors.black,
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
      lineHeight: 1.5,
      fontSize: 1,
      color: baseColors.white,
      fontWeight: "500",
      zIndex: 1000
    }
  },

  forms: {
    label: {
      ...formBase,
      fontWeight: "600",
      fontSize: 1
    },

    unit: {
      ...formCell,

      textAlign: "center",
      bg: "muted",
      fontWeight: "600",
      borderRadius: "0 8px 8px 0"
    },

    input: {
      ...formCell,

      flex: 1,
      borderRadius: "8px 0 0 8px",
      transition: "all 0.2s ease",

      ":focus": {
        outline: "none",
        borderColor: baseColors.yellow_200,
        boxShadow: `0 0 0 3px ${baseColors.yellow_100}`
      }
    },

    editor: {}
  },

  layout: {
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "stretch",

      "@media screen and (min-width: 48em)": {
        borderBottom: "1px solid #E5E7EB"
      },

      position: ["fixed", "relative"],
      width: "100vw",
      top: 0,
      zIndex: 1000,

      px: [2, "12px", "12px", 5],
      py: [2, "12px", "12px"],

      ...headerGradient,
      boxShadow: ["0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)", "none"]
    },

    footer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      mt: cardGapY,
      px: 3,
      minHeight: "72px",

      bg: "muted"
    },

    main: {
      width: "100%",
      maxWidth: "1280px",
      mx: "auto",
      mt: ["40px", 0],
      mb: ["40px", "40px"],
      px: cardGapX
    },

    columns: {
      display: "flex",
      flexWrap: "wrap",
      justifyItems: "center"
    },

    left: {
      pr: cardGapX,
      width: ["100%", "58%"]
    },

    right: {
      width: ["100%", "42%"]
    },

    actions: {
      justifyContent: "flex-end",
      mt: 2,
      flexWrap: "nowrap",
      gap: 2,

      button: {
        ml: 0,
        whiteSpace: "nowrap"
      }
    },

    disabledOverlay: {
      ...overlay,

      bg: "rgba(255, 255, 255, 0.5)"
    },

    modalOverlay: {
      ...modalOverlay,

      bg: "rgba(255, 255, 255, 0.5)",
      zIndex: 999,
      backdropFilter: "blur(7px)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    },

    modal: {
      padding: 3,
      width: ["100%", "40em"]
    },

    infoOverlay: {
      ...modalOverlay,

      display: "none",
      "@media screen and (max-width: 70em)": {
        display: "block"
      },
      // display: ["block", "none"],

      bg: "rgba(255, 255, 255, 0.8)"
    },

    infoMessage: {
      display: "flex",
      justifyContent: "center",
      m: 3,
      alignItems: "center",
      minWidth: "128px"
    },

    sidenav: {
      // display: ["flex", "none"],
      display: "none",
      "@media screen and (max-width: 70em)": {
        display: "flex"
      },
      flexDirection: "column",
      p: 0,
      m: 0,
      borderColor: "muted",
      mr: "25vw",
      height: "100%",
      ...headerGradient
    },

    badge: {
      border: 0,
      borderRadius: "8px",
      p: 1,
      px: 2,
      backgroundColor: baseColors.yellow_100,
      color: baseColors.black,
      fontSize: 0,
      fontWeight: "600",
      letterSpacing: "0.5px"
    }
  },

  styles: {
    root: {
      fontFamily: "body",
      lineHeight: "body",
      fontWeight: "body",

      height: "100%",

      "#root": {
        height: "100%"
      }
    },

    a: {
      color: baseColors.blue,
      textDecoration: "none",
      fontWeight: "600",
      transition: "color 0.2s ease",
      ":hover": {
        color: "accent",
        textDecoration: "underline"
      }
    }
  },

  links: {
    nav: {
      px: 3,
      py: 2,
      color: "#6B7280",
      fontWeight: "600",
      fontSize: 1,
      textDecoration: "none",
      borderRadius: "8px",
      transition: "all 0.2s ease",
      width: ["100%", "auto"],
      mt: [3, "auto"],

      "@media screen and (max-width: 70em)": {
        display: "flex",
        flexDirection: "column",
        mt: 3
      },

      ":hover": {
        color: baseColors.black,
        bg: baseColors.yellow_100
      },

      "&.active": {
        color: baseColors.black,
        fontWeight: "700",
        bg: baseColors.yellow_100
      }
    }
  }
};

export default theme;
